import type { ToolMap } from '@toolproof-core/lib/_pure/macro-step-decisions';
import type { SpaceDerivationFrame } from '@toolproof-core/visualization/derivation/_contract/derivation';
import { resolveWorkflowStateEntryResolution } from '@/workflow/_pure/derivation/workflowStateEntryResolution';
import { buildWorkflowStateEntryItemKey } from '@/workflow/_pure/keyCodec';
import { selectActiveToolStepFromTraceSnapshot } from '../model/traceSnapshotSelectors';
import { resolveOperationPresentationMode } from '../config/config';
import type { TraceSnapshotView } from '../../_contract/traceSnapshot';
import type { Config } from '../../_contract/config';

type Operand = { role: string; itemKey: string; value: string };
export type OperationCaptionModel = {
    name: string;
    inputs: readonly Operand[];
    outputs: readonly Operand[];
};
type Caption = NonNullable<NonNullable<SpaceDerivationFrame['spaceRenderOptions']>['operationCaption']>;

const INFIX: Readonly<Record<string, { roles: readonly [string, string]; sign: string }>> = {
    NaturalAdd: { roles: ['AddendOne', 'AddendTwo'], sign: '+' },
    NaturalSubtract: { roles: ['Minuend', 'Subtrahend'], sign: '-' },
    NaturalMultiply: { roles: ['Multiplicand', 'Multiplier'], sign: '*' },
    NaturalLessThan: { roles: ['LessThanSource', 'LessThanTarget'], sign: '<' },
};

export function operationCaptionModel(snapshot: TraceSnapshotView | undefined, toolMap: ToolMap,
    stepKey?: string, renderedStepKey = stepKey): OperationCaptionModel | undefined {
    if (!snapshot) return undefined;
    const step = stepKey ? snapshot.position.orderedToolStepsBeforeBranchExitPrune.find(step => step.stepKey === stepKey)
        : selectActiveToolStepFromTraceSnapshot(snapshot);
    if (!step) {
        if (stepKey) throw new Error(`Operation caption ToolStep '${stepKey}' is absent.`);
        return undefined;
    }
    const tool = toolMap.get(step.toolHandle);
    if (!tool) throw new Error(`Operation caption Tool '${step.toolHandle}' is absent.`);
    const roles = (direction: 'input' | 'output'): Operand[] => Object.entries(direction === 'input'
        ? tool.roleSpec.inputRoleByName : tool.roleSpec.outputRoleByName).map(([role, spec]) => {
        const resolved = resolveWorkflowStateEntryResolution({ direction, stepKey: step.stepKey, roleName: role,
            workflowState: snapshot.position.workflowState });
        return { role, itemKey: buildWorkflowStateEntryItemKey({ stepKey: renderedStepKey ?? step.stepKey, direction,
            roleName: role, resourceTypeHandle: spec.resourceTypeHandle }), value: resolved.projectionText ?? '?' };
    });
    return { name: step.toolHandle.replace(/^TOOL-/, ''), inputs: roles('input'), outputs: roles('output') };
}

function captionText(model: OperationCaptionModel, value: (operand: Operand) => string, running: boolean): string {
    const infix = INFIX[model.name];
    let expression: string;
    if (infix && model.outputs.length === 1) {
        const operands = infix.roles.map(role => {
            const operand = model.inputs.find(input => input.role === role);
            if (!operand) throw new Error(`Operation caption '${model.name}' requires role '${role}'.`);
            return value(operand);
        });
        expression = `${operands[0]} ${running ? infix.sign : ' '} ${operands[1]} = ${value(model.outputs[0]!)}`;
    } else {
        const roles = (operands: readonly Operand[]) => operands.map(operand => `${operand.role}=${value(operand)}`).join(', ');
        expression = `${roles(model.inputs)} ${running ? '->' : '  '} ${roles(model.outputs)}`;
    }
    return `${model.name}: ${expression}`;
}

export function formatOperationCaption(model: OperationCaptionModel, revealed: ReadonlySet<string>,
    running: boolean): Caption {
    return {
        text: captionText(model, operand => revealed.has(operand.itemKey) ? operand.value : '?', running),
        layoutText: captionText(model, operand => operand.value, true),
        targetItemKeys: [...model.inputs, ...model.outputs].map(operand => operand.itemKey),
    };
}

export function withOperationCaption(frame: SpaceDerivationFrame, config: Config,
    snapshot: TraceSnapshotView | undefined, toolMap: ToolMap): SpaceDerivationFrame {
    const enabled = config.adapterModeConfig.showOperationCaption === true && resolveOperationPresentationMode(config) === 'panels';
    const model = enabled ? operationCaptionModel(snapshot, toolMap) : undefined;
    const caption = model ? formatOperationCaption(model,
        new Set([...model.inputs, ...model.outputs].map(operand => operand.itemKey)), true) : null;
    return { ...frame, spaceRenderOptions: { ...frame.spaceRenderOptions, operationCaption: caption,
        operationCaptionEnabled: enabled } };
}
